import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import adminFetch from "../../utils/adminFetch";
import { useSocket } from "../../hooks/useSocket";
import AssignmentDropdown from "../../components/admin/AssignmentDropdown";
import ActivityNotes from "../../components/admin/ActivityNotes";


function timeAgo(str) {
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  return new Date(str).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const STATUS_OPTIONS = ["pending", "contacted", "completed"];
const STATUS_CONFIG  = {
  pending:   { label: "Pending",   color: "#92400E", bg: "#FEF3C7" },
  contacted: { label: "Contacted", color: "#1D4ED8", bg: "#DBEAFE" },
  completed: { label: "Completed", color: "#065F46", bg: "#D1FAE5" },
};


const PAGE_SIZE = 15;


function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "240px" }}>
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}


// ── Detail Modal ──────────────────────────────────────────────────────
function DetailModal({ c, onClose, onStatusChange, onDelete }) {
  const [updating,    setUpdating]    = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [assignedTo,  setAssignedTo]  = useState(c.assignedTo || {});
  const cfg = STATUS_CONFIG[c.status] || {};


  const handleStatusChange = async (status) => {
    setUpdating(true);
    await onStatusChange(c._id, status);
    setUpdating(false);
  };


  const handleDelete = async () => {
    if (!window.confirm("Delete this consultation request? This cannot be undone.")) return;
    setDeleting(true);
    await onDelete(c._id);
    setDeleting(false);
    onClose();
  };


  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
        backgroundColor: "rgba(1,4,20,0.5)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: "540px",
          backgroundColor: "#F7F4EF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(1,46,114,0.22)",
          border: "1px solid rgba(1,46,114,0.1)",
          display: "flex", // dinagdag
          flexDirection: "column", // 2
          maxHeight: "90vh", // 3
        }}
      >
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #012E72, #002DB5)",
          padding: "22px 28px",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px",
        }}>
          <div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.28em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: "5px" }}>
              Consultation Request
            </p>
            <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: "#fff", letterSpacing: "0.04em", lineHeight: 1.2 }}>
              {c.fullName}
            </h2>
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(255,255,255,0.55)", marginTop: "4px" }}>
              {timeAgo(c.createdAt)}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "rgba(255,255,255,0.45)", transition: "color 0.15s", flexShrink: 0, marginTop: "2px",
          }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>

        {/* Body */}
        <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Contact info */}
          <div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "12px" }}>
              Contact
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              {[
                ["Email",   c.email,   "mailto:" + c.email],
                ["Phone",   c.phone,   "tel:" + c.phone],
                ["Address", c.address, null],
              ].filter(([, v]) => v).map(([label, value, href]) => (
                <div key={label} style={{ display: "flex", gap: "14px", alignItems: "baseline", padding: "10px 0", borderBottom: "1px solid rgba(1,46,114,0.07)" }}>
                  <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", flexShrink: 0, width: "64px" }}>
                    {label}
                  </span>
                  {href ? (
                    <a href={href} style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#002DB5", textDecoration: "none" }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                      onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
                      {value}
                    </a>
                  ) : (
                    <span style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72" }}>{value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>


          {/* Video call button */}
          {c.contactMethod === "video" && (
            <div>
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "10px" }}>
                Video Consultation
              </p>
              <div style={{ display: "flex", gap: "10px" }}>
                <a
                  href={`/video-call?channel=consultation-${c._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #012E72, #002DB5)", border: "none", borderRadius: "10px", padding: "10px 18px", cursor: "pointer", textDecoration: "none", boxShadow: "0 4px 14px rgba(0,45,181,0.25)" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                  </svg>
                  Start Video Call
                </a>
                <button
                  onClick={() => {
                    const link = `${window.location.origin}/video-call?channel=consultation-${c._id}`;
                    navigator.clipboard.writeText(link);
                  }}
                  style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#012E72", background: "rgba(1,46,114,0.06)", border: "1px solid rgba(1,46,114,0.12)", borderRadius: "10px", padding: "10px 18px", cursor: "pointer" }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                  Copy Client Link
                </button>
              </div>
              <p style={{ fontFamily: "'Constantia', serif", fontSize: "11px", color: "rgba(1,46,114,0.4)", marginTop: "8px" }}>
                Share the client link with <strong>{c.fullName}</strong> to join the same call.
              </p>
            </div>
          )}


          {/* Notes */}
          {c.details && (
            <div>
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "10px" }}>
                Notes
              </p>
              <p style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72", lineHeight: 1.75, backgroundColor: "#fff", borderRadius: "12px", padding: "14px 16px", border: "1px solid rgba(1,46,114,0.09)" }}>
                {c.details}
              </p>
            </div>
          )}


          {/* Status */}
          <div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "10px" }}>
              Status
            </p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {STATUS_OPTIONS.map(s => {
                const active = c.status === s;
                const scfg   = STATUS_CONFIG[s];
                return (
                  <button
                    key={s}
                    onClick={() => !active && handleStatusChange(s)}
                    disabled={updating}
                    style={{
                      fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em",
                      textTransform: "uppercase", border: "none", borderRadius: "8px",
                      padding: "9px 18px", cursor: active ? "default" : "pointer",
                      backgroundColor: active ? scfg.bg    : "rgba(1,46,114,0.05)",
                      color:           active ? scfg.color : "rgba(1,46,114,0.35)",
                      fontWeight:      active ? 700 : 400,
                      outline:         active ? `2px solid ${scfg.color}30` : "none",
                      outlineOffset:   "2px",
                      opacity: updating ? 0.6 : 1,
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { if (!active && !updating) { e.currentTarget.style.backgroundColor = scfg.bg; e.currentTarget.style.color = scfg.color; } }}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.05)"; e.currentTarget.style.color = "rgba(1,46,114,0.35)"; } }}
                  >
                    {scfg.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>


        {/* Assignment + Activity Notes */}
        <div style={{ padding: "0 28px 20px", display: "flex", flexDirection: "column", gap: "20px" }}>
          <AssignmentDropdown
            entityType="consultations"
            entityId={c._id}
            assignedTo={assignedTo}
            onAssigned={setAssignedTo}
          />
          <ActivityNotes
            entityType="consultations"
            entityId={c._id}
            notes={c.activityNotes || []}
          />
        </div>
      </div>

        {/* Footer */}
        <div style={{ padding: "16px 28px", borderTop: "1px solid rgba(1,46,114,0.08)", backgroundColor: "rgba(1,46,114,0.02)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#DC2626", border: "1px solid rgba(220,38,38,0.22)", borderRadius: "999px", padding: "8px 18px", cursor: deleting ? "not-allowed" : "pointer", backgroundColor: "transparent", opacity: deleting ? 0.4 : 1, transition: "all 0.15s" }}
            onMouseEnter={e => { if (!deleting) { e.currentTarget.style.backgroundColor = "#DC2626"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#DC2626"; } }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.borderColor = "rgba(220,38,38,0.22)"; }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
          <button onClick={onClose} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #012E72, #002DB5)", border: "none", borderRadius: "999px", padding: "9px 24px", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,45,181,0.25)" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Main Page ─────────────────────────────────────────────────────────
export default function AdminConsultations() {
  usePageTitle("Consultations");
  const navigate = useNavigate();
  const socketRef = useSocket(); // ← shared socket from AdminLayout


  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [filter,     setFilter]     = useState("all");
  const [search,     setSearch]     = useState("");
  const [selected,   setSelected]   = useState(null);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [counts,     setCounts]     = useState({});
  const [dateFrom,   setDateFrom]   = useState("");
  const [dateTo,     setDateTo]     = useState("");
  const searchTimer = useRef(null);


  const loadItems = useCallback(async (pg, srch, flt, from, to) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE });
      if (srch) params.set("search", srch);
      if (flt && flt !== "all") params.set("status", flt);
      if (from) params.set("from", from);
      if (to)   params.set("to", to);
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/consultations?${params}`);
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Failed to load consultations.");
      const json = await res.json();
      setItems(json.data);
      setTotal(json.total);
      setTotalPages(json.pages);
      setCounts(json.counts || {});

    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [navigate]);


  useEffect(() => { loadItems(1, "", "all", "", ""); }, [loadItems]);


  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); loadItems(1, search, filter, dateFrom, dateTo); }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => { loadItems(page, search, filter, dateFrom, dateTo); }, [filter, page]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => { setPage(1); loadItems(1, search, filter, dateFrom, dateTo); }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Socket listeners ─────────────────────────────────────────────────
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;


    // New consultation submitted — prepend to list if on page 1 with no filters
    function onNew(doc) {
      if (page === 1 && !search && filter === "all" && !dateFrom && !dateTo) {
        setItems(prev => {
          // Avoid duplicates (socket may fire twice in dev StrictMode)
          if (prev.some(c => c._id === doc._id)) return prev;
          const updated = [doc, ...prev].slice(0, PAGE_SIZE);
          return updated;
        });
        setTotal(prev => prev + 1);
        setCounts(prev => ({ ...prev, pending: (prev.pending || 0) + 1 }));
      } else {
        // On any other view just do a silent refresh so counts stay accurate
        loadItems(page, search, filter, dateFrom, dateTo);
      }
    }


    // Status updated — patch in place in the list and in the open modal
    function onUpdated({ id, status }) {
      setItems(prev => prev.map(c => c._id === id ? { ...c, status } : c));
      setSelected(prev => prev?._id === id ? { ...prev, status } : prev);
      // Recalculate counts by doing a lightweight re-fetch
      loadItems(page, search, filter, dateFrom, dateTo);
    }


    // Deleted — remove from list and close modal if it was open
    function onDeleted({ id }) {
      setItems(prev => prev.filter(c => c._id !== id));
      setSelected(prev => prev?._id === id ? null : prev);
      setTotal(prev => Math.max(0, prev - 1));
    }


    socket.on("consultation:new",     onNew);
    socket.on("consultation:updated", onUpdated);
    socket.on("consultation:deleted", onDeleted);


    return () => {
      socket.off("consultation:new",     onNew);
      socket.off("consultation:updated", onUpdated);
      socket.off("consultation:deleted", onDeleted);
    };
  }, [socketRef, page, search, filter, dateFrom, dateTo, loadItems]);
  // ─────────────────────────────────────────────────────────────────────


  const updateStatus = async (id, status) => {
    try {
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/consultations/${id}`, {
        method: "PATCH", body: JSON.stringify({ status }),
      });
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Status update failed.");
      // Optimistic update already handled by socket event; no extra setState needed
    } catch (err) { alert(err.message); }
  };


  const deleteItem = async (id) => {
    try {
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/consultations/${id}`, { method: "DELETE" });
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Delete failed.");
      // List update handled by socket event
    } catch (err) { alert(err.message); }
  };


  const exportCSV = async () => {
    const params = new URLSearchParams({ page: 1, limit: 1000 });
    if (search) params.set("search", search);
    if (filter && filter !== "all") params.set("status", filter);
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo)   params.set("to", dateTo);
    const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/consultations?${params}`);
    const json = await res.json();
    const all  = json.data || [];
    const headers = ["Full Name", "Email", "Phone", "Address", "Details", "Status", "Submitted"];
    const escape = v => { const s = v == null ? "" : String(v); return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = all.map(c => [c.fullName, c.email, c.phone, c.address, c.details, c.status, new Date(c.createdAt).toLocaleDateString("en-US")]);
    const csv = [headers.join(","), ...rows.map(r => r.map(escape).join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "consultations.csv"; a.click();
  };


  const paginated  = items;
  const safePage   = page;
  const selectedLive = selected ? items.find(c => c._id === selected._id) || selected : null;


  if (loading) return <Spinner />;
  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "240px", gap: "12px" }}>
      <p style={{ fontFamily: "'Constantia', serif", color: "#dc2626", fontSize: "14px" }}>{error}</p>
      <button onClick={() => window.location.reload()} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#012E72", border: "1px solid rgba(1,46,114,0.25)", borderRadius: "999px", padding: "8px 20px", cursor: "pointer", backgroundColor: "transparent" }}>Retry</button>
    </div>
  );


  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>


      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "12px" }}>
        <div>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.26em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", marginBottom: "6px" }}>Admin / Consultations</p>
          <h1 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px", letterSpacing: "0.04em", color: "#012E72", lineHeight: 1 }}>Consultations</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", paddingBottom: "2px" }}>
            {total} total · {counts.pending || 0} pending
          </p>
          {items.length > 0 && (
            <button onClick={exportCSV} style={{ display: "flex", alignItems: "center", gap: "6px", fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#012E72", border: "1px solid rgba(1,46,114,0.2)", borderRadius: "999px", padding: "8px 16px", cursor: "pointer", backgroundColor: "transparent", transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#012E72"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#012E72"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#012E72"; e.currentTarget.style.borderColor = "rgba(1,46,114,0.2)"; }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export CSV
            </button>
          )}
        </div>
      </div>


      {/* ── Stat pills ── */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {[
          { label: "Total",     value: total,              color: "#012E72", bg: "rgba(1,46,114,0.07)" },
          { label: "Pending",   value: counts.pending   || 0, color: "#92400E", bg: "#FEF3C7" },
          { label: "Contacted", value: counts.contacted || 0, color: "#1D4ED8", bg: "#DBEAFE" },
          { label: "Completed", value: counts.completed || 0, color: "#065F46", bg: "#D1FAE5" },
        ].map(s => (
          <div key={s.label} style={{ backgroundColor: s.bg, borderRadius: "10px", padding: "10px 18px", display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: s.color }}>{s.value}</span>
            <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.16em", textTransform: "uppercase", color: s.color, opacity: 0.7 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Search + Filters ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: "200px", maxWidth: "300px" }}>
          <svg style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text" placeholder="Search name, email, phone…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", paddingLeft: "34px", paddingRight: search ? "32px" : "14px", paddingTop: "9px", paddingBottom: "9px", fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "999px", backgroundColor: "#fff", outline: "none", boxSizing: "border-box" }}
            onFocus={e => e.target.style.borderColor = "#002DB5"}
            onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: "16px", lineHeight: 1 }}>×</button>
          )}
        </div>


        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "999px", padding: "8px 12px", outline: "none", backgroundColor: "#fff", cursor: "pointer" }}
            onFocus={e => e.target.style.borderColor = "#002DB5"} onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"} />
          <span style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", color: "#94a3b8" }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "999px", padding: "8px 12px", outline: "none", backgroundColor: "#fff", cursor: "pointer" }}
            onFocus={e => e.target.style.borderColor = "#002DB5"} onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"} />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#94a3b8", background: "none", border: "none", cursor: "pointer" }}>×</button>
          )}
        </div>


        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {["all", ...STATUS_OPTIONS].map(s => {
            const active = filter === s;
            const count  = s === "all" ? total : counts[s] || 0;
            return (
              <button key={s} onClick={() => setFilter(s)} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", padding: "8px 16px", borderRadius: "999px", cursor: "pointer", transition: "all 0.15s", backgroundColor: active ? "#012E72" : "#fff", color: active ? "#fff" : "#6B7280", border: `1px solid ${active ? "#012E72" : "rgba(1,46,114,0.12)"}` }}>
                {s === "all" ? "All" : STATUS_CONFIG[s].label}
                {count > 0 && <span style={{ marginLeft: "6px", opacity: 0.65 }}>({count})</span>}
              </button>
            );
          })}
        </div>
      </div>


      {/* ── Table ── */}
      <div style={{ backgroundColor: "#fff", borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(1,46,114,0.09)", boxShadow: "0 2px 16px rgba(1,46,114,0.06)" }}>
        {items.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 32px", gap: "10px" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "12px", backgroundColor: "rgba(1,46,114,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#012E72" }}>No results found</p>
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", fontStyle: "italic" }}>
              {search ? `No matches for "${search}"` : "No consultation requests here yet."}
            </p>
            {search && (
              <button onClick={() => setSearch("")} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#012E72", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", opacity: 0.6 }}>Clear search</button>
            )}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(1,46,114,0.07)" }}>
                  {["Name", "Email", "Phone", "Submitted", "Status", ""].map((h, i) => (
                    <th key={i} style={{ padding: "13px 18px", textAlign: "left", fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", backgroundColor: "rgba(1,46,114,0.025)", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((c, i) => {
                  const cfg = STATUS_CONFIG[c.status] || {};
                  return (
                    <tr
                      key={c._id}
                      onClick={() => setSelected(c)}
                      style={{ borderBottom: i < paginated.length - 1 ? "1px solid rgba(1,46,114,0.055)" : "none", cursor: "pointer", transition: "background-color 0.12s" }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.025)"}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      <td style={{ padding: "15px 18px", whiteSpace: "nowrap" }}>
                        <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "13px", color: "#012E72" }}>{c.fullName}</span>
                      </td>
                      <td style={{ padding: "15px 18px", maxWidth: "200px" }}>
                        <span style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.55)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</span>
                      </td>
                      <td style={{ padding: "15px 18px", whiteSpace: "nowrap" }}>
                        <span style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.55)" }}>{c.phone || "—"}</span>
                      </td>
                      <td style={{ padding: "15px 18px", whiteSpace: "nowrap" }}>
                        <span style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.38)" }}>{timeAgo(c.createdAt)}</span>
                      </td>
                      <td style={{ padding: "15px 18px" }}>
                        <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.1em", textTransform: "uppercase", color: cfg.color, backgroundColor: cfg.bg, borderRadius: "6px", padding: "4px 10px", whiteSpace: "nowrap" }}>
                          {cfg.label}
                        </span>
                      </td>
                      <td style={{ padding: "15px 14px" }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.25)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.4)" }}>
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, total)} of {total}
          </p>
          <div style={{ display: "flex", gap: "6px" }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cinzel', serif", fontSize: "13px", border: "1px solid rgba(1,46,114,0.12)", color: "#012E72", backgroundColor: "#fff", cursor: safePage === 1 ? "not-allowed" : "pointer", opacity: safePage === 1 ? 0.35 : 1 }}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
              .reduce((acc, n, idx, arr) => { if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…"); acc.push(n); return acc; }, [])
              .map((n, i) => n === "…" ? (
                <span key={`e-${i}`} style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.3)" }}>…</span>
              ) : (
                <button key={n} onClick={() => setPage(n)} style={{ width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cinzel', serif", fontSize: "11px", cursor: "pointer", border: `1px solid ${safePage === n ? "#012E72" : "rgba(1,46,114,0.12)"}`, backgroundColor: safePage === n ? "#012E72" : "#fff", color: safePage === n ? "#fff" : "#012E72" }}>{n}</button>
              ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cinzel', serif", fontSize: "13px", border: "1px solid rgba(1,46,114,0.12)", color: "#012E72", backgroundColor: "#fff", cursor: safePage === totalPages ? "not-allowed" : "pointer", opacity: safePage === totalPages ? 0.35 : 1 }}>›</button>
          </div>
        </div>
      )}


      {/* ── Detail Modal ── */}
      {selectedLive && (
        <DetailModal
          c={selectedLive}
          onClose={() => setSelected(null)}
          onStatusChange={updateStatus}
          onDelete={async (id) => { await deleteItem(id); setSelected(null); }}
        />
      )}
    </div>
  );
}

